import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking and performance monitoring
 * Configure once at application startup
 */
export function initSentry() {
  const env = (window as any).__ENV ?? {};
  const sentryDsn = env.VITE_SENTRY_DSN ?? import.meta.env.VITE_SENTRY_DSN;
  if (!sentryDsn) {
    console.debug('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment: env.VITE_ENVIRONMENT ?? import.meta.env.MODE,
    tracesSampleRate: (env.VITE_ENVIRONMENT ?? import.meta.env.MODE) === 'production' ? 0.1 : 1.0,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    release: env.VITE_APP_VERSION ?? import.meta.env.VITE_APP_VERSION ?? 'unknown',
    // Before sending to Sentry, filter out sensitive data
    beforeSend(event, _hint) {
      // Filter out errors from third-party scripts
      const orig = (_hint as any)?.originalException;
      if (orig instanceof Error) {
        const message = orig.message || '';
        // Ignore known noisy errors
        if (
          message.includes('Non-Error promise rejection') ||
          message.includes('ResizeObserver loop limit exceeded')
        ) {
          return null;
        }
      }

      // Remove sensitive data from URLs
      if (event.request?.url) {
        event.request.url = event.request.url.replace(/token=[\w-]+/g, 'token=***');
      }

      return event;
    },
    // Before sending breadcrumbs
    beforeBreadcrumb(breadcrumb, _hint) {
      // Filter out noisy console logs
      if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
        return null;
      }

      // Filter out socket events that are too noisy
      if (breadcrumb.category === 'user-interaction' && breadcrumb.message?.includes('socket')) {
        return null;
      }

      return breadcrumb;
    },
  });

  console.log('[Sentry] Error tracking initialized with DSN:', sentryDsn);
}

/**
 * Capture exception with custom context
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (context) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Capture message event
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string = 'user-action',
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, any>,
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Set user context for error tracking
 */
export function setUserContext(userId: string, email?: string, username?: string) {
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
}

/**
 * Clear user context on logout
 */
export function clearUserContext() {
  Sentry.setUser(null);
}

/**
 * Higher-order component for error boundary
 */
export const ErrorBoundary = Sentry.ErrorBoundary;

/**
 * Set custom tags for filtering errors
 */
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * Get Sentry instance
 */
export function getSentry() {
  return Sentry;
}
